"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

const emptyMap = new Map<string, string>();

function readHreflangTags(): Map<string, string> {
  if (typeof document === "undefined") return emptyMap;

  const map = new Map<string, string>();
  const links = document.head.querySelectorAll<HTMLLinkElement>(
    'link[rel="alternate"][hreflang]',
  );

  for (const link of links) {
    const lang = link.getAttribute("hreflang");
    const href = link.getAttribute("href");
    if (!lang || !href || lang === "x-default") continue;

    try {
      const url = new URL(href, globalThis.location.origin);
      map.set(lang, url.pathname);
    } catch {
      // skip malformed URLs
    }
  }

  return map.size > 0 ? map : emptyMap;
}

/**
 * Reads `<link rel="alternate" hreflang="...">` tags from the document head
 * and returns a map of language code to pathname (origin stripped).
 * Uses a MutationObserver on <head> so it picks up tags that Next.js
 * streams in after the initial render / client-side navigation.
 */
export function useAlternateLinks(): Map<string, string> {
  const pathname = usePathname();
  const [alternates, setAlternates] = useState<Map<string, string>>(emptyMap);

  useEffect(() => {
    let settled = false;

    const update = () => {
      const next = readHreflangTags();
      if (next !== emptyMap) {
        settled = true;
        setAlternates(next);
      }
    };

    const observer = new MutationObserver(update);
    observer.observe(document.head, { childList: true, subtree: true });

    update();

    return () => {
      observer.disconnect();
      if (!settled) setAlternates(emptyMap);
    };
  }, [pathname]);

  return alternates;
}
